import { medicalApiPaths, medicalsApi } from "../../../../api/medicalsApi";
import { formatDateTime } from "../../../../utils/DateUtils";
import { CONDITION_CATEGORY } from "../../Constants";
import { vitalSignsCategory, vitalSignsCode } from "../cards/vitals/constants";

const acceptedChiefComplaintStatuses = [
  "registered",
  "preliminary",
  "final",
  "amended",
];

export const fetchComplaints = async (encounterId, patientId) => {
  const query = {
    encounter: encounterId,
    subject: patientId,
    status: acceptedChiefComplaintStatuses,
    category: "chief-complaint",
  };
  return medicalsApi.findEntities(query, medicalApiPaths.observations);
};

export const fetchAllergies = async (patientId, encounterId?, query = {}) => {
  let dbQuery: Record<string, unknown> = { ...query, patient: patientId };
  if (encounterId) {
    dbQuery = { ...dbQuery, encounter: encounterId };
  }
  return medicalsApi.findAllergies(dbQuery);
};

export const fetchPatientHistory = async (patientId, ended: string) => {
  const query = { patient: patientId, "meta-end": ended };
  return medicalsApi.getPatientHistory(query);
};

export const fetchMedicalConditions = async (patientId) => {
  if (!patientId) return;
  return medicalsApi.findEntities(
    {
      patient: patientId,
      clinicalStatus: ["active", "recurrence", "relapse", "remission"],
      verificationStatus: [
        "unconfirmed",
        "provisional",
        "differential",
        "confirmed",
      ],
    },
    medicalApiPaths.conditions,
  );
};

export const fetchEncounterDiagnoses = async (patientId, encounterId) => {
  if (patientId) {
    const query = {
      encounter: encounterId,
      category: CONDITION_CATEGORY.ENCOUNTER_DIAGNOSIS,
      "excluded-verification-status": "entered-in-error",
      // clinicalStatus: ["active", "recurrence", "relapse"],
    };
    return medicalsApi.findEntities(query, medicalApiPaths.conditions);
  }
};

export const fetchCurrentMedications = async (patientId) => {
  const query = {
    patient: patientId,
    status: ["active", "intended", "stopped", "on-hold", "completed"],
  };
  return medicalsApi.findMedicationStatements(query);
};

export const fetchProgressNotes = async (patientId, encounterId) => {
  const query = {
    patient: patientId,
    encounter: encounterId,
    "exclude-types": ["55107-7"],
  };
  return medicalsApi.findEntities(query, "compositions");
};

export const fetchAddendums = async (patientId, encounterId) => {
  const query = {
    patient: patientId,
    encounter: encounterId,
    type: "55107-7",
  };
  return medicalsApi.findEntities(query, "compositions");
};

export const fetchVitals = async (patientId, encounterId) => {
  const query = {
    subject: patientId,
    code: vitalSignsCode,
    category: vitalSignsCategory,
    encounter: encounterId,
  };

  return medicalsApi.findEntities(query, medicalApiPaths.observations);
};

export const createVitalDisplay = (data) => {
  const fragments = [];
  if (data) {
    data.forEach((d) => {
      const entries = d?.hasMember?.map((m) => m.display);
      const effDate = new Date(d.meta.created);
      entries?.unshift(formatDateTime(effDate));
      fragments.push({
        text: entries?.join(" · "),
        id: d.id,
        final: d.status && d.status === "final",
      });
    });
  }
  return fragments;
};

const singleColumnTemplate = [
  "ChiefComplaints",
  "Vitals",
  "InitialAssessments",
  "Resuscitation",
  "Allergies",
  "ImmunizationFeature",
  "PatientHistories",
  "CurrentMedications",
  "ReviewOfSystems",
  "PhysicalAssessments",
  "Attachments",
  "ProgressNotes",
  "Procedures",
  "Diagnoses",
  // "FraminghamRisk",
];

const dualColumnTemplate = [
  "ChiefComplaints",
  "none",
  "Vitals",
  "InitialAssessments",
  "none",
  "ReviewOfSystems",
  "Resuscitation",
  "none",
  "ImmunizationFeature",
  "PhysicalAssessments",
  "Allergies",
  "none",
  "PatientHistories",
  "none",
  "CurrentMedications",
  "Attachments",
  "none",
  "ProgressNotes",
  "none",
  "Addendums",
  "none",
  "Procedures",
  "none",
  "Diagnoses",
  // "FraminghamRisk",
];

/** Cards that ship enabled without a LaunchDarkly allow* flag. */
const ALWAYS_ON_ENCOUNTER_CARDS = new Set(["FraminghamRisk"]);

const isEncounterCardAllowed = (
  cardKey: string,
  flags: Record<string, unknown>,
) =>
  ALWAYS_ON_ENCOUNTER_CARDS.has(cardKey) || Boolean(flags[`allow${cardKey}`]);

export const setSingleColumns = (flags) => {
  const columns = [];
  singleColumnTemplate.forEach((t) => {
    if (isEncounterCardAllowed(t, flags)) {
      columns.push(t);
    } else columns.push("none");
  });

  return columns;
};

export const setDualColumns = (flags) => {
  const columns = [];
  dualColumnTemplate.forEach((t) => {
    if (isEncounterCardAllowed(t, flags)) {
      columns.push(t);
    } else columns.push("none");
  });

  return columns;
};

export const getRandomInt = (max?: number): number =>
  Math.floor(Math.random() * (max ?? 1000000));
